
import { Link } from 'react-router-dom';
import { useState } from 'react';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import SEO from '../../components/common/SEO';
import Breadcrumbs from '../../components/common/Breadcrumbs';
import RelatedGuides from '../../components/common/RelatedGuides';
import {
  quickTakeData,
  travelOptionsData,
  costBreakdownData,
  villagesData,
  timelineData,
  proTips,
  commonMistakes,
  faqData,
  schemaData
} from './data';

export default function AntiguaToLakeAtitlanPage() {
  const [openFaqIndex, setOpenFaqIndex] = useState<number | null>(null);

  const breadcrumbItems = [
    { label: 'Home', path: '/' },
    { label: 'Day Trips', path: '/day-trips-from-antigua-guatemala' },
    { label: 'Antigua to Lake Atitl\u00e1n' }
  ];

  const relatedGuides = [
    {
      title: 'Day Trips from Antigua',
      path: '/day-trips-from-antigua-guatemala',
      description: 'All the best day trips from Antigua Guatemala'
    },
    {
      title: 'Antigua to Chichicastenango',
      path: '/antigua-to-chichicastenango',
      description: 'Visit Guatemala\'s most famous indigenous market'
    },
    {
      title: 'Pacaya Volcano Day Trip',
      path: '/pacaya-volcano-day-trip-from-antigua',
      description: 'Active lava hike from Antigua'
    },
    {
      title: 'Acatenango Hike',
      path: '/acatenango-hike-from-antigua',
      description: 'Overnight volcano trek with Fuego views'
    },
    {
      title: '2-Week Guatemala Itinerary',
      path: '/antigua-guatemala-2-week-itinerary',
      description: 'Complete itinerary including Lake Atitl\u00e1n'
    },
    {
      title: 'Best Time to Visit Antigua',
      path: '/best-time-to-visit-antigua-guatemala',
      description: 'Seasonal weather and travel recommendations'
    }
  ];

  return (
    <>
      <SEO
        title="Antigua to Lake Atitl\u00e1n: Complete Shuttle & Day Trip Guide (2025)"
        description="Everything you need to know about visiting Lake Atitl\u00e1n from Antigua Guatemala: shuttle options, costs, best villages, boat rides, and whether a day trip is worth it."
        canonical="https://la-antigua-guatemala.com/antigua-to-lake-atitlan-shuttle"
        keywords="antigua to lake atitlan, antigua to lake atitlan shuttle, lake atitlan day trip from antigua, how to get from antigua to lake atitlan, antigua to panajachel, lake atitlan guatemala, lake atitlan villages, antigua guatemala day trips"
        type="article"
        schema={schemaData}
        lastModified="2025-01-20"
        ogImage={{
          url: 'https://public.readdy.ai/ai/img_res/f220b15ed9fcc526fab792192928fb84.jpg',
          width: '1200',
          height: '630'
        }}
      />

      <div className="min-h-screen bg-white">
        <Navbar darkText />

        <div className="pt-20">
          {/* Hero Section */}
          <section className="relative h-[500px] w-full">
            <img
              src="https://readdy.ai/api/search-image?query=Lake%20Atitlan%20Guatemala%20breathtaking%20panoramic%20view%20with%20three%20majestic%20volcanoes%20rising%20from%20crystal%20blue%20water%2C%20traditional%20colorful%20boats%20on%20shore%2C%20lush%20green%20hillsides%2C%20indigenous%20village%20with%20terracotta%20roofs%2C%20golden%20morning%20light%2C%20stunning%20natural%20landscape%20photography&width=1920&height=500&seq=atitlan-hero-001&orientation=landscape"
              alt="Panoramic view of Lake Atitl\u00e1n with three volcanoes and traditional boats"
              className="w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/50"></div>
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="text-center text-white px-4 max-w-4xl w-full">
                <h1 className="text-5xl md:text-6xl font-bold mb-4">
                  Antigua to Lake Atitl&aacute;n
                </h1>
                <p className="text-xl md:text-2xl font-light">
                  Complete guide to visiting the most beautiful lake in the world
                </p>
              </div>
            </div>
          </section>

          {/* Main Content */}
          <div className="max-w-4xl mx-auto px-4 py-12">
            <Breadcrumbs items={breadcrumbItems} />

            {/* Quick Take Box */}
            <div className="bg-teal-50 border-l-4 border-teal-600 p-6 my-8 rounded-r-lg">
              <h2 className="text-2xl font-bold text-teal-900 mb-4">Quick Take</h2>
              <div className="grid md:grid-cols-2 gap-4 text-gray-800">
                <div>
                  <p className="font-semibold text-teal-800">Distance:</p>
                  <p>{quickTakeData.distance}</p>
                </div>
                <div>
                  <p className="font-semibold text-teal-800">Travel Time:</p>
                  <p>{quickTakeData.travelTime}</p>
                </div>
                <div>
                  <p className="font-semibold text-teal-800">Best For:</p>
                  <p>{quickTakeData.bestFor}</p>
                </div>
                <div>
                  <p className="font-semibold text-teal-800">Ideal Duration:</p>
                  <p>{quickTakeData.idealDuration}</p>
                </div>
                <div className="md:col-span-2">
                  <p className="font-semibold text-teal-800">Skip If:</p>
                  <p>{quickTakeData.skipIf}</p>
                </div>
              </div>
            </div>

            {/* Is It Worth It Section */}
            <section className="mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-6">
                Is Lake Atitl&aacute;n Worth Visiting from Antigua?
              </h2>

              <p className="text-lg text-gray-700 mb-4">
                <strong>Absolutely yes.</strong> Lake Atitl&aacute;n is widely considered one of the most beautiful lakes in the world, and for good reason. Surrounded by three towering volcanoes&mdash;San Pedro (3,020m), Tolim&aacute;n (3,158m), and Atitl&aacute;n (3,537m)&mdash;the lake sits in a massive volcanic caldera at 5,100 feet elevation. The combination of deep blue water, dramatic volcanic peaks, and traditional Maya villages clinging to the hillsides creates scenery that\u2019s genuinely breathtaking.
              </p>

              <p className="text-lg text-gray-700 mb-4">
                Aldous Huxley famously wrote that Lake Atitl&aacute;n was &ldquo;the most beautiful lake in the world&rdquo; in his 1934 book <em>Beyond the Mexique Bay</em>. Nearly a century later, the description still holds. The lake is a must-see for anyone visiting Guatemala, and Antigua is the most convenient base for a day trip.
              </p>

              <p className="text-lg text-gray-700 mb-4">
                That said, a day trip is rushed. You\u2019ll spend 5&ndash;6 hours driving and have only 4&ndash;5 hours at the lake. If you can spare two days, staying overnight at the lake is far better. But if a day trip is all you have, it\u2019s still absolutely worth the journey. For help fitting Lake Atitl&aacute;n into your schedule, check our <Link to="/antigua-guatemala-2-week-itinerary" className="text-teal-600 hover:text-teal-700 font-medium">2-week Guatemala itinerary</Link>.
              </p>
            </section>

            {/* How to Get There Section */}
            <section className="mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-6">
                How to Get from Antigua to Lake Atitl&aacute;n
              </h2>

              <p className="text-lg text-gray-700 mb-4">
                Lake Atitl&aacute;n is approximately 75 miles (120 km) west of Antigua. The drive follows the Pan-American Highway (CA-1) through the Guatemalan highlands, passing through Chimaltenango and the Los Encuentros junction before descending to Solol&aacute; and down to Panajachel on the lake\u2019s north shore.
              </p>

              <p className="text-lg text-gray-700 mb-6">
                The road is paved but winding, with stunning highland views along the way. Here are your transport options:
              </p>

              <div className="overflow-x-auto mb-6">
                <table className="w-full border-collapse border border-gray-300">
                  <thead>
                    <tr className="bg-teal-600 text-white">
                      <th className="border border-gray-300 px-4 py-3 text-left">Option</th>
                      <th className="border border-gray-300 px-4 py-3 text-left">Cost</th>
                      <th className="border border-gray-300 px-4 py-3 text-left">Comfort</th>
                      <th className="border border-gray-300 px-4 py-3 text-left">Travel Time</th>
                      <th className="border border-gray-300 px-4 py-3 text-left">Pros</th>
                      <th className="border border-gray-300 px-4 py-3 text-left">Cons</th>
                    </tr>
                  </thead>
                  <tbody>
                    {travelOptionsData.map((option, index) => (
                      <tr key={index} className={index % 2 === 0 ? 'bg-gray-50' : 'bg-white'}>
                        <td className="border border-gray-300 px-4 py-3 font-semibold">{option.option}</td>
                        <td className="border border-gray-300 px-4 py-3">{option.cost}</td>
                        <td className="border border-gray-300 px-4 py-3">{option.comfort}</td>
                        <td className="border border-gray-300 px-4 py-3">{option.travelTime}</td>
                        <td className="border border-gray-300 px-4 py-3 text-sm">{option.pros}</td>
                        <td className="border border-gray-300 px-4 py-3 text-sm">{option.cons}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="space-y-6">
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">Organized Tour (Best for Day Trips)</h3>
                  <p className="text-gray-700 mb-2">
                    Tours cost $40&ndash;$60 and include round-trip transport from Antigua, a bilingual guide, boat rides between villages, and visits to 2&ndash;3 lakeside communities. Your guide provides cultural context about the Tz&rsquo;utujil and Kaqchikel Maya peoples who live around the lake.
                  </p>
                  <p className="text-gray-700">
                    Best for: First-time visitors, those who want a hassle-free experience with everything arranged.
                  </p>
                </div>

                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">Shuttle Service (Best Value)</h3>
                  <p className="text-gray-700 mb-2">
                    Shuttles cost $25&ndash;$35 round-trip and drop you in Panajachel. From there, you arrange your own boat rides and village visits. Shuttles depart Antigua around 6&ndash;7 AM and return around 3&ndash;4 PM. Book through your hotel or agencies like Atitrans, Adrenalina Tours, or GuateGo.
                  </p>
                  <p className="text-gray-700">
                    Best for: Independent travelers comfortable navigating on their own, budget-conscious visitors.
                  </p>
                </div>

                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">Private Driver (Most Flexible)</h3>
                  <p className="text-gray-700 mb-2">
                    Private drivers cost $90&ndash;$140 for the day and offer complete flexibility. You choose departure time, how long you stay, and can stop at the stunning Solol&aacute; viewpoint overlooking the lake. Great for groups of 3&ndash;4 splitting costs.
                  </p>
                  <p className="text-gray-700">
                    Best for: Groups, travelers with specific schedules, those combining with <Link to="/antigua-to-chichicastenango" className="text-teal-600 hover:text-teal-700 font-medium">Chichicastenango</Link>.
                  </p>
                </div>

                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">Chicken Bus (Budget Adventure)</h3>
                  <p className="text-gray-700 mb-2">
                    Chicken buses cost $3&ndash;$6 but require transfers at Chimaltenango and Los Encuentros, take 4+ hours each way, and don&rsquo;t align well with a day trip schedule. Only recommended for experienced budget travelers with flexible timelines.
                  </p>
                  <p className="text-gray-700">
                    Best for: Experienced travelers comfortable with local transport who are staying overnight at the lake.
                  </p>
                </div>
              </div>
            </section>

            {/* Timeline Section */}
            <section className="mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-6">
                Typical Day Trip Timeline
              </h2>

              <p className="text-lg text-gray-700 mb-6">
                Here&rsquo;s what a standard Lake Atitl&aacute;n day trip from Antigua looks like:
              </p>

              <div className="space-y-4">
                {timelineData.map((item, index) => (
                  <div key={index} className="flex items-start border-l-4 border-teal-600 pl-4 py-2">
                    <div className="min-w-[140px]">
                      <p className="font-bold text-teal-700">{item.time}</p>
                    </div>
                    <div className="flex-1">
                      <p className="text-gray-900 font-medium">{item.activity}</p>
                      {item.duration !== '\u2014' && (
                        <p className="text-sm text-gray-600">({item.duration})</p>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              <p className="text-gray-700 mt-6 italic">
                Note: Times vary by tour operator and traffic. Some tours include a stop at the Solol&aacute; viewpoint for panoramic photos of the lake.
              </p>
            </section>

            {/* Villages Section */}
            <section className="mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-6">
                Best Villages to Visit at Lake Atitl&aacute;n
              </h2>

              <p className="text-lg text-gray-700 mb-6">
                Lake Atitl&aacute;n has over a dozen villages scattered around its shores, each with its own character. For a day trip, you can realistically visit 2&ndash;3. Here are the top options:
              </p>

              <div className="space-y-8">
                {villagesData.map((village, index) => (
                  <div key={index} className="bg-gray-50 rounded-lg overflow-hidden">
                    <div className="p-6">
                      <div className="flex items-start justify-between mb-3">
                        <div>
                          <h3 className="text-2xl font-bold text-gray-900">{village.name}</h3>
                          <p className="text-teal-600 font-medium text-sm">{village.nickname}</p>
                        </div>
                        <span className="bg-teal-100 text-teal-700 px-3 py-1 rounded-full text-xs font-semibold whitespace-nowrap">
                          {village.timeNeeded}
                        </span>
                      </div>
                      <p className="text-gray-700 mb-4">{village.description}</p>
                      <div className="grid sm:grid-cols-2 gap-3">
                        <div className="flex items-start">
                          <i className="ri-user-line text-teal-500 mt-1 mr-2"></i>
                          <div>
                            <strong className="text-gray-900 text-sm">Best for:</strong>
                            <span className="text-gray-700 text-sm ml-1">{village.bestFor}</span>
                          </div>
                        </div>
                        <div className="flex items-start">
                          <i className="ri-star-line text-teal-500 mt-1 mr-2"></i>
                          <div>
                            <strong className="text-gray-900 text-sm">Highlight:</strong>
                            <span className="text-gray-700 text-sm ml-1">{village.highlight}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              <div className="bg-teal-50 border-l-4 border-teal-500 p-6 mt-8 rounded-r-lg">
                <h3 className="text-xl font-bold text-gray-900 mb-3">Our Recommended Day Trip Route</h3>
                <p className="text-gray-700 mb-2">
                  <strong>For culture lovers:</strong> Panajachel &rarr; San Juan La Laguna &rarr; Santiago Atitl&aacute;n &rarr; Panajachel
                </p>
                <p className="text-gray-700 mb-2">
                  <strong>For relaxation:</strong> Panajachel &rarr; San Marcos La Laguna &rarr; San Juan La Laguna &rarr; Panajachel
                </p>
                <p className="text-gray-700">
                  <strong>For backpackers:</strong> Panajachel &rarr; San Pedro La Laguna &rarr; San Juan La Laguna &rarr; Panajachel
                </p>
              </div>
            </section>

            {/* What to Expect Section */}
            <section className="mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-6">
                What to Expect at Lake Atitl&aacute;n
              </h2>

              <h3 className="text-2xl font-bold text-gray-900 mb-4">The Lake Itself</h3>

              <p className="text-gray-700 mb-4">
                Lake Atitl&aacute;n is a volcanic crater lake measuring roughly 18 km long and 8 km wide, with a maximum depth of 340 meters (1,120 feet). The water is a deep, mesmerizing blue that shifts color throughout the day&mdash;turquoise in the morning, deep indigo by afternoon.
              </p>

              <p className="text-gray-700 mb-4">
                Three volcanoes dominate the southern shore: San Pedro, Tolim&aacute;n, and Atitl&aacute;n. On clear mornings, their reflections in the still water create one of Guatemala&rsquo;s most iconic views. By afternoon, the <em>Xocomil</em> wind often picks up, creating whitecaps and making boat rides choppier.
              </p>

              <h3 className="text-2xl font-bold text-gray-900 mb-4 mt-8">Boat Rides Between Villages</h3>

              <p className="text-gray-700 mb-4">
                Getting between villages is done by <em>lancha</em>&mdash;small motorboats that zip across the lake. Public lanchas depart from the main dock in Panajachel when full (every 20&ndash;30 minutes) and cost $3&ndash;$5 per person per ride. Rides take 15&ndash;45 minutes depending on the destination.
              </p>

              <p className="text-gray-700 mb-4">
                The boat rides themselves are a highlight&mdash;speeding across the deep blue water with volcanoes towering on all sides is exhilarating. Sit on the left side of the boat for the best volcano views heading south.
              </p>

              <h3 className="text-2xl font-bold text-gray-900 mb-4 mt-8">Indigenous Culture</h3>

              <p className="text-gray-700 mb-4">
                The villages around Lake Atitl&aacute;n are home to Tz&rsquo;utujil and Kaqchikel Maya communities who maintain their languages, traditional dress, and cultural practices. Each village has its own distinct style of <em>huipil</em> (traditional blouse) and cultural identity.
              </p>

              <p className="text-gray-700 mb-4">
                In San Juan La Laguna, women&rsquo;s cooperatives demonstrate traditional backstrap loom weaving and natural dye techniques using plants, insects, and minerals. In Santiago Atitl&aacute;n, you can visit the shrine of Maximon, a syncretic deity that blends Maya and Catholic traditions.
              </p>

              <h3 className="text-2xl font-bold text-gray-900 mb-4 mt-8">Food at the Lake</h3>

              <p className="text-gray-700 mb-4">
                Lakeside restaurants in Panajachel and the villages offer a mix of Guatemalan and international cuisine. Expect to pay $8&ndash;$15 for a full meal with a view. Try the <em>pepian</em> (traditional Guatemalan stew), fresh fish from the lake, or the excellent coffee grown on surrounding hillsides.
              </p>
            </section>

            {/* Mid-Page CTA */}
            <div className="bg-gradient-to-r from-teal-600 to-teal-700 text-white p-8 rounded-lg my-12 text-center">
              <h2 className="text-3xl font-bold mb-4">Planning Your Guatemala Trip?</h2>
              <p className="text-xl mb-6">
                Get a personalized itinerary that includes Lake Atitl&aacute;n, volcanoes, and all the best experiences around Antigua.
              </p>
              <Link
                to="/plan-my-antigua-trip"
                className="inline-block bg-white text-teal-700 px-8 py-4 rounded-lg font-bold text-lg hover:bg-gray-100 transition whitespace-nowrap cursor-pointer"
              >
                Plan My Trip
              </Link>
            </div>

            {/* Day Trip vs Overnight Section */}
            <section className="mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-6">
                Day Trip vs. Overnight: Which Is Better?
              </h2>

              <div className="grid md:grid-cols-2 gap-6 mb-6">
                <div className="bg-gray-50 p-6 rounded-lg border-t-4 border-teal-500">
                  <h3 className="text-xl font-bold text-gray-900 mb-4">Day Trip</h3>
                  <ul className="space-y-2 text-gray-700 text-sm">
                    <li className="flex items-start">
                      <i className="ri-check-line text-teal-500 mt-0.5 mr-2"></i>
                      <span>See the lake and 2&ndash;3 villages in one day</span>
                    </li>
                    <li className="flex items-start">
                      <i className="ri-check-line text-teal-500 mt-0.5 mr-2"></i>
                      <span>No need to pack overnight bags</span>
                    </li>
                    <li className="flex items-start">
                      <i className="ri-check-line text-teal-500 mt-0.5 mr-2"></i>
                      <span>Keep your Antigua hotel as base</span>
                    </li>
                    <li className="flex items-start">
                      <i className="ri-close-line text-rose-500 mt-0.5 mr-2"></i>
                      <span>5&ndash;6 hours of driving</span>
                    </li>
                    <li className="flex items-start">
                      <i className="ri-close-line text-rose-500 mt-0.5 mr-2"></i>
                      <span>Only 4&ndash;5 hours at the lake</span>
                    </li>
                    <li className="flex items-start">
                      <i className="ri-close-line text-rose-500 mt-0.5 mr-2"></i>
                      <span>Feels rushed, limited village time</span>
                    </li>
                  </ul>
                  <p className="mt-4 text-sm font-semibold text-teal-700">Best for: Travelers with limited time who still want to see the lake</p>
                </div>

                <div className="bg-gray-50 p-6 rounded-lg border-t-4 border-amber-500">
                  <h3 className="text-xl font-bold text-gray-900 mb-4">Overnight (Recommended)</h3>
                  <ul className="space-y-2 text-gray-700 text-sm">
                    <li className="flex items-start">
                      <i className="ri-check-line text-teal-500 mt-0.5 mr-2"></i>
                      <span>Experience sunrise and sunset over the lake</span>
                    </li>
                    <li className="flex items-start">
                      <i className="ri-check-line text-teal-500 mt-0.5 mr-2"></i>
                      <span>Visit 4&ndash;5 villages at a relaxed pace</span>
                    </li>
                    <li className="flex items-start">
                      <i className="ri-check-line text-teal-500 mt-0.5 mr-2"></i>
                      <span>Swim, kayak, or hike Indian Nose</span>
                    </li>
                    <li className="flex items-start">
                      <i className="ri-check-line text-teal-500 mt-0.5 mr-2"></i>
                      <span>Enjoy lakeside nightlife and restaurants</span>
                    </li>
                    <li className="flex items-start">
                      <i className="ri-close-line text-rose-500 mt-0.5 mr-2"></i>
                      <span>Requires extra night of accommodation ($15&ndash;$80)</span>
                    </li>
                    <li className="flex items-start">
                      <i className="ri-close-line text-rose-500 mt-0.5 mr-2"></i>
                      <span>Uses 2 days of your itinerary</span>
                    </li>
                  </ul>
                  <p className="mt-4 text-sm font-semibold text-amber-700">Best for: Anyone with 5+ days in Guatemala</p>
                </div>
              </div>

              <p className="text-gray-700">
                <strong>Our recommendation:</strong> If you have 5 or more days in Guatemala, stay overnight. The lake deserves at least a full day of exploration. If you only have 3&ndash;4 days total, a day trip still gives you a taste of this incredible place.
              </p>
            </section>

            {/* Pro Tips Section */}
            <section className="mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-6">
                Pro Tips for Visiting Lake Atitl&aacute;n
              </h2>

              <div className="grid md:grid-cols-2 gap-6">
                {proTips.map((tip, index) => (
                  <div key={index} className="bg-amber-50 border-l-4 border-amber-500 p-5 rounded-r-lg">
                    <h3 className="font-bold text-amber-900 mb-2">{tip.title}</h3>
                    <p className="text-gray-800 text-sm">{tip.description}</p>
                  </div>
                ))}
              </div>
            </section>

            {/* Best Time Section */}
            <section className="mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-6">
                Best Time to Visit Lake Atitl&aacute;n
              </h2>

              <div className="bg-white border border-gray-200 p-8 rounded-lg shadow-sm mb-8">
                <h3 className="text-2xl font-bold text-gray-900 mb-4">Dry Season (November&ndash;April)</h3>
                <p className="text-gray-700 mb-4">
                  <strong>Best for:</strong> Clear views, calm lake, comfortable boat rides
                </p>
                <ul className="space-y-2 text-gray-700">
                  <li className="flex items-start">
                    <i className="ri-check-line text-teal-500 mt-1 mr-2"></i>
                    <span>Crystal-clear mornings with perfect volcano reflections</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-check-line text-teal-500 mt-1 mr-2"></i>
                    <span>Calm water for smooth boat rides</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-check-line text-teal-500 mt-1 mr-2"></i>
                    <span>Warm days (70&ndash;80&deg;F / 21&ndash;27&deg;C)</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-alert-line text-amber-500 mt-1 mr-2"></i>
                    <span>Peak tourist season&mdash;more crowds and higher prices</span>
                  </li>
                </ul>
              </div>

              <div className="bg-white border border-gray-200 p-8 rounded-lg shadow-sm mb-8">
                <h3 className="text-2xl font-bold text-gray-900 mb-4">Rainy Season (May&ndash;October)</h3>
                <p className="text-gray-700 mb-4">
                  <strong>Best for:</strong> Fewer crowds, lower prices, lush green scenery
                </p>
                <ul className="space-y-2 text-gray-700">
                  <li className="flex items-start">
                    <i className="ri-check-line text-teal-500 mt-1 mr-2"></i>
                    <span>Mornings are usually clear&mdash;leave early</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-check-line text-teal-500 mt-1 mr-2"></i>
                    <span>Fewer tourists and better prices on accommodation</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-alert-line text-amber-500 mt-1 mr-2"></i>
                    <span>Afternoon rain is common (usually 2&ndash;5 PM)</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-alert-line text-amber-500 mt-1 mr-2"></i>
                    <span>Clouds may obscure volcano views by midday</span>
                  </li>
                </ul>
              </div>

              <p className="text-gray-700">
                For more seasonal planning, see our <Link to="/best-time-to-visit-antigua-guatemala" className="text-teal-600 hover:text-teal-700 font-medium">best time to visit Antigua guide</Link>.
              </p>
            </section>

            {/* Safety Section */}
            <section className="mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-6">
                Is Lake Atitl&aacute;n Safe?
              </h2>

              <p className="text-lg text-gray-700 mb-4">
                Yes, the main tourist villages around Lake Atitl&aacute;n are safe for visitors. Panajachel, San Pedro, San Juan, San Marcos, and Santiago are all well-traveled and accustomed to tourists. However, like anywhere in Guatemala, basic precautions apply.
              </p>

              <h3 className="text-2xl font-bold text-gray-900 mb-4 mt-8">Safety Tips</h3>

              <ul className="space-y-3 text-gray-700 mb-6">
                <li className="flex items-start">
                  <span className="text-teal-600 mr-2 mt-1">&check;</span>
                  <span>Use registered boats from the main docks&mdash;avoid unlicensed operators</span>
                </li>
                <li className="flex items-start">
                  <span className="text-teal-600 mr-2 mt-1">&check;</span>
                  <span>Don&rsquo;t walk between villages on isolated paths&mdash;take boats instead</span>
                </li>
                <li className="flex items-start">
                  <span className="text-teal-600 mr-2 mt-1">&check;</span>
                  <span>Keep valuables secure, especially on crowded boats and in markets</span>
                </li>
                <li className="flex items-start">
                  <span className="text-teal-600 mr-2 mt-1">&check;</span>
                  <span>Avoid boat rides in strong afternoon winds (Xocomil)</span>
                </li>
                <li className="flex items-start">
                  <span className="text-teal-600 mr-2 mt-1">&check;</span>
                  <span>Travel with organized tours or shuttles rather than alone if it&rsquo;s your first visit</span>
                </li>
              </ul>

              <p className="text-gray-700">
                For more general safety information, see our <Link to="/is-antigua-guatemala-safe" className="text-teal-600 hover:text-teal-700 font-medium">Antigua safety guide</Link>.
              </p>
            </section>

            {/* Common Mistakes Section */}
            <section className="mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-6">
                Common Mistakes Visitors Make
              </h2>

              <div className="space-y-3">
                {commonMistakes.map((mistake, index) => (
                  <div key={index} className="flex items-start bg-rose-50 border-l-4 border-rose-500 p-4 rounded-r-lg">
                    <span className="text-rose-600 mr-3 mt-1 text-xl">&times;</span>
                    <p className="text-gray-800">{mistake}</p>
                  </div>
                ))}
              </div>
            </section>

            {/* Cost Breakdown Section */}
            <section className="mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-6">
                Cost Breakdown: How Much Does a Lake Atitl&aacute;n Day Trip Cost?
              </h2>

              <p className="text-lg text-gray-700 mb-6">
                Here&rsquo;s what to budget for a day trip from Antigua to Lake Atitl&aacute;n:
              </p>

              <div className="overflow-x-auto mb-6">
                <table className="w-full border-collapse border border-gray-300">
                  <thead>
                    <tr className="bg-teal-600 text-white">
                      <th className="border border-gray-300 px-4 py-3 text-left">Item</th>
                      <th className="border border-gray-300 px-4 py-3 text-left">Cost (USD)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {costBreakdownData.map((item, index) => (
                      <tr key={index} className={index % 2 === 0 ? 'bg-gray-50' : 'bg-white'}>
                        <td className="border border-gray-300 px-4 py-3">{item.item}</td>
                        <td className="border border-gray-300 px-4 py-3 font-semibold">{item.cost}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <p className="text-gray-700 mb-4">
                <strong>Budget travelers:</strong> Expect $50&ndash;$70 total (shuttle + boats + simple lunch).
              </p>
              <p className="text-gray-700 mb-4">
                <strong>Mid-range travelers:</strong> Plan for $70&ndash;$100 (tour + lunch + shopping).
              </p>
              <p className="text-gray-700">
                <strong>Comfort travelers:</strong> Budget $120&ndash;$180+ (private driver + nice lunch + activities).
              </p>
            </section>

            {/* Combining with Chichicastenango */}
            <section className="mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-6">
                Combining Lake Atitl&aacute;n with Chichicastenango
              </h2>

              <p className="text-lg text-gray-700 mb-4">
                Lake Atitl&aacute;n and <Link to="/antigua-to-chichicastenango" className="text-teal-600 hover:text-teal-700 font-medium">Chichicastenango Market</Link> are in the same region, making it tempting to combine them. Here&rsquo;s how:
              </p>

              <div className="space-y-6">
                <div className="border-l-4 border-teal-500 pl-4">
                  <h3 className="text-xl font-bold text-gray-900 mb-2">Option 1: Overnight Trip (Recommended)</h3>
                  <p className="text-gray-700">
                    Day 1: Antigua &rarr; Chichicastenango Market (Thursday or Sunday) &rarr; Lake Atitl&aacute;n (stay overnight). Day 2: Explore lake villages &rarr; return to Antigua. Total driving: ~6 hours spread over 2 days.
                  </p>
                </div>

                <div className="border-l-4 border-teal-500 pl-4">
                  <h3 className="text-xl font-bold text-gray-900 mb-2">Option 2: Very Long Day Trip</h3>
                  <p className="text-gray-700">
                    Antigua &rarr; Chichicastenango (3 hours at market) &rarr; Lake Atitl&aacute;n (2 hours at lake) &rarr; Antigua. Leave by 5:30 AM, return by 9 PM. Exhausting but doable with a private driver ($120&ndash;$160).
                  </p>
                </div>

                <div className="border-l-4 border-teal-500 pl-4">
                  <h3 className="text-xl font-bold text-gray-900 mb-2">Option 3: Separate Day Trips</h3>
                  <p className="text-gray-700">
                    Visit each on different days for the best experience. This requires 2 full days but lets you properly enjoy both destinations. See our <Link to="/antigua-guatemala-2-week-itinerary" className="text-teal-600 hover:text-teal-700 font-medium">2-week itinerary</Link> for how to structure this.
                  </p>
                </div>
              </div>
            </section>

            {/* What to Pack Section */}
            <section className="mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-6">
                What to Bring for a Lake Atitl&aacute;n Day Trip
              </h2>

              <div className="grid md:grid-cols-2 gap-6">
                <div className="bg-gray-50 p-6 rounded-lg">
                  <h3 className="text-xl font-bold text-gray-900 mb-4">Essentials</h3>
                  <ul className="space-y-2 text-gray-700 text-sm">
                    <li className="flex items-start">
                      <i className="ri-check-line text-teal-500 mt-0.5 mr-2"></i>
                      <span>Sunscreen (SPF 50+&mdash;UV is intense at altitude)</span>
                    </li>
                    <li className="flex items-start">
                      <i className="ri-check-line text-teal-500 mt-0.5 mr-2"></i>
                      <span>Sunglasses</span>
                    </li>
                    <li className="flex items-start">
                      <i className="ri-check-line text-teal-500 mt-0.5 mr-2"></i>
                      <span>Light jacket or windbreaker</span>
                    </li>
                    <li className="flex items-start">
                      <i className="ri-check-line text-teal-500 mt-0.5 mr-2"></i>
                      <span>Cash in small bills (quetzales)</span>
                    </li>
                    <li className="flex items-start">
                      <i className="ri-check-line text-teal-500 mt-0.5 mr-2"></i>
                      <span>Waterproof bag for electronics</span>
                    </li>
                    <li className="flex items-start">
                      <i className="ri-check-line text-teal-500 mt-0.5 mr-2"></i>
                      <span>Comfortable walking shoes</span>
                    </li>
                  </ul>
                </div>

                <div className="bg-gray-50 p-6 rounded-lg">
                  <h3 className="text-xl font-bold text-gray-900 mb-4">Optional</h3>
                  <ul className="space-y-2 text-gray-700 text-sm">
                    <li className="flex items-start">
                      <i className="ri-check-line text-teal-500 mt-0.5 mr-2"></i>
                      <span>Swimsuit (for swimming at San Marcos)</span>
                    </li>
                    <li className="flex items-start">
                      <i className="ri-check-line text-teal-500 mt-0.5 mr-2"></i>
                      <span>Camera with zoom lens (for volcano shots)</span>
                    </li>
                    <li className="flex items-start">
                      <i className="ri-check-line text-teal-500 mt-0.5 mr-2"></i>
                      <span>Rain jacket (rainy season)</span>
                    </li>
                    <li className="flex items-start">
                      <i className="ri-check-line text-teal-500 mt-0.5 mr-2"></i>
                      <span>Reusable water bottle</span>
                    </li>
                    <li className="flex items-start">
                      <i className="ri-check-line text-teal-500 mt-0.5 mr-2"></i>
                      <span>Motion sickness medication (for winding roads)</span>
                    </li>
                    <li className="flex items-start">
                      <i className="ri-check-line text-teal-500 mt-0.5 mr-2"></i>
                      <span>Small daypack</span>
                    </li>
                  </ul>
                </div>
              </div>
            </section>

            {/* FAQ Section */}
            <section className="mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-6">
                Frequently Asked Questions
              </h2>

              <div className="space-y-4">
                {faqData.map((faq, index) => (
                  <div key={index} className="border border-gray-300 rounded-lg overflow-hidden">
                    <button
                      onClick={() => setOpenFaqIndex(openFaqIndex === index ? null : index)}
                      className="w-full flex items-center justify-between p-5 bg-gray-50 hover:bg-gray-100 transition text-left cursor-pointer"
                    >
                      <h3 className="font-bold text-gray-900 pr-4">{faq.question}</h3>
                      <span className="text-teal-600 text-2xl flex-shrink-0">
                        {openFaqIndex === index ? '\u2212' : '+'}
                      </span>
                    </button>
                    {openFaqIndex === index && (
                      <div className="p-5 bg-white border-t border-gray-300">
                        <p className="text-gray-700">{faq.answer}</p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </section>

            {/* Final CTA Section */}
            <div className="bg-gradient-to-r from-gray-900 to-gray-800 text-white p-8 rounded-lg my-12">
              <h2 className="text-3xl font-bold mb-4 text-center">Ready to Visit Lake Atitl&aacute;n?</h2>
              <p className="text-xl mb-6 text-center max-w-2xl mx-auto">
                Let us help you plan the perfect Guatemala trip that includes Lake Atitl&aacute;n, volcano hikes, and all the best Antigua experiences.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link
                  to="/plan-my-antigua-trip"
                  className="inline-block bg-teal-500 hover:bg-teal-600 text-white px-8 py-4 rounded-lg font-bold text-lg transition text-center whitespace-nowrap cursor-pointer"
                >
                  Plan My Trip
                </Link>
                <Link
                  to="/antigua-guatemala-2-week-itinerary"
                  className="inline-block bg-white hover:bg-gray-100 text-gray-900 px-8 py-4 rounded-lg font-bold text-lg transition text-center whitespace-nowrap cursor-pointer"
                >
                  View 2-Week Itinerary
                </Link>
              </div>
            </div>

            {/* Related Guides */}
            <RelatedGuides guides={relatedGuides} />
          </div>
        </div>

        <Footer />
      </div>
    </>
  );
}
